import { Core } from "@faire/design-tokens";
import { IBrand } from "@faire/web-api/indigofair/data/IBrand";
import { IBrandVideo } from "@faire/web-api/indigofair/video/IBrandVideo";
import { localDate } from "@faire/web/common/localization";
import {
  DEPRECATED_backwardsCompatibleValues,
  useStrictLocalization,
} from "@faire/web/common/localization/localize";
import {
  formatMoney,
  moneyOrZero,
  RoundingStrategy,
} from "@faire/web/common/money";
import { trackEvent } from "@faire/web/common/trackEvent";
import { Button } from "@faire/web/slate/Button";
import { Checkmark } from "@faire/web/slate/icons/Checkmark";
import { Information } from "@faire/web/slate/icons/foundation/Information";
import { Column, HorizontalLine, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Tooltip } from "@faire/web/slate/Tooltip";
import { Typography } from "@faire/web/slate/Typography";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { BrandAvatar } from "@faire/retailer/components/BrandAvatar";
import { TrackedLink } from "@faire/retailer/components/TrackedLink";
import { getBrandLocationString } from "@faire/retailer/consts/getBrandLocationString";
import {
  followBrand,
  trackBoardsEvent,
  unFollowBrand,
} from "@faire/retailer/features/RetailerBoards/utils";
import { formatVideoDuration } from "@faire/retailer/lib/formatVideoDuration";
import {
  canMessageBrand,
  getUnverifiedRetailerTooltipLabels,
  showMessageBrandCTA,
  showVerificationTooltip,
} from "@faire/retailer/lib/messageBrandCTAUtils";
import { brandPage } from "@faire/retailer/paths";
import { useMessengerFactory } from "@faire/retailer/stores/MessengerFactory";

import { LazyContactBrandModal } from "../brand/ContactBrand/modal";

export const BRAND_PANEL_WIDTH = 420;

interface IProps {
  brand: IBrand;
  brandVideo: IBrandVideo;
}

export const BrandPanel: React.FC<IProps> = ({ brand, brandVideo }) => {
  const { strictLocalize } = useStrictLocalization();
  const [showContactModal, setShowContactModal] = useState(false);
  const [isFollowing, setIsFollowing] = useState(brand.is_following);
  const viewport = useViewport();

  const { messengerStore } = useMessengerFactory(["messengerStore"]);

  useEffect(() => {
    messengerStore.setCurrentBrandContext(brand);
    return () => {
      messengerStore.setCurrentBrandContext(undefined);
    };
  }, [brand, messengerStore]);

  const handleSetBrandFavorite = useCallback(async () => {
    if (!brand?.token || !brand?.name) {
      return;
    }
    setIsFollowing(true);
    await followBrand(brand.token, brand.name);
    trackBoardsEvent(`brand/${brand.token}/follow/brand`);
  }, [brand?.token, brand?.name]);

  const handleRemoveBrandFavorite = useCallback(() => {
    if (!brand?.token || !brand?.name) {
      return;
    }
    setIsFollowing(false);
    unFollowBrand(brand.token);
    trackBoardsEvent(`brand/${brand.token}/unfollow/brand`);
  }, [brand?.token, brand?.name]);

  const toggleFollowButton = useCallback(() => {
    const newIsFollowing = !isFollowing;
    setIsFollowing(!newIsFollowing);
    if (newIsFollowing) {
      handleSetBrandFavorite();
    } else {
      handleRemoveBrandFavorite();
    }
  }, [isFollowing, handleSetBrandFavorite, handleRemoveBrandFavorite]);

  const toggleContactModal = useCallback(() => {
    const newShowContactModal = !showContactModal;
    setShowContactModal(newShowContactModal);

    if (newShowContactModal) {
      trackEvent("show-contact-modal", true);
    }
  }, [showContactModal]);

  const renderButtons = useCallback(() => {
    const unverifiedTooltipLabels =
      getUnverifiedRetailerTooltipLabels(strictLocalize);
    return (
      <>
        {showMessageBrandCTA(brand) ? (
          <>
            <StyledButton
              onClick={toggleContactModal}
              disabled={!canMessageBrand(brand)}
            >
              <StrictLocalMsg
                defaultMessage="Message"
                description={{ text: "Prompt to send a message to a brand" }}
              />
              {showVerificationTooltip(brand) ? (
                <>
                  <Spacer size="1x" />
                  <StyledTooltip
                    position="bottom"
                    icon={<Information width="12px" height="12px" />}
                    contents={[
                      {
                        title: unverifiedTooltipLabels.title,
                        body: unverifiedTooltipLabels.message,
                      },
                    ]}
                  />
                </>
              ) : null}
            </StyledButton>
            <Spacer size="2x" />
          </>
        ) : null}

        <StyledButton onClick={toggleFollowButton}>
          {isFollowing ? (
            <>
              <StrictLocalMsg
                defaultMessage="Following"
                description={{
                  text: "Similar to how you follow people on social media",
                }}
              />
              <StyledCheckmark />
            </>
          ) : (
            <StrictLocalMsg
              defaultMessage="Follow"
              description={{
                text: "Similar to how you follow people on social media",
              }}
            />
          )}
        </StyledButton>
      </>
    );
  }, [
    brand,
    isFollowing,
    strictLocalize,
    toggleContactModal,
    toggleFollowButton,
  ]);

  const profileImage = brand?.profile_image;

  const renderSmallerVideoName =
    !viewport.isDesktopAndAbove || (brandVideo.name?.length || 0) > 40;

  const basedIn = getBrandLocationString(
    {
      city: brand?.based_in_city,
      state: brand?.based_in_state,
    },
    strictLocalize
  );
  return (
    <>
      <PanelContainer data-test-id="video-brand-panel">
        <PanelContent>
          <Row align="center">
            {profileImage && profileImage.url ? (
              <>
                <TrackedLink to={brandPage(brand.token!)} token={brand.token}>
                  <BrandAvatar
                    image={profileImage}
                    size={viewport.isTabletAndAbove ? "large" : "small"}
                    isTopShop={brand.is_top_shop}
                  />
                </TrackedLink>
                <Spacer size="2x" />
              </>
            ) : null}
            <BrandDetailsWrapper justify="space-between">
              <BrandDetails>
                <BrandNameWrapper>
                  <TrackedLink to={brandPage(brand.token!)} token={brand.token}>
                    <Typography
                      color={Core.text.default}
                      truncate
                      variant="paragraphSansMedium"
                    >
                      {brand?.name}
                    </Typography>
                  </TrackedLink>
                </BrandNameWrapper>
                <Typography color={Core.text.subdued} truncate>
                  {basedIn}
                </Typography>
                <Typography color={Core.text.subdued} truncate>
                  <StrictLocalMsg
                    defaultMessage="{count, plural, one{# Product} other{# Products}}"
                    values={DEPRECATED_backwardsCompatibleValues({
                      count: brand?.active_products_count ?? 1,
                    })}
                  />
                  {" · "}
                  <StrictLocalMsg
                    defaultMessage="{moneyAmount} Min"
                    description={{
                      text: "[money amount] minimum, where min is short for minimum and represents how much needs to be purchased to order from this brand",
                    }}
                    values={DEPRECATED_backwardsCompatibleValues({
                      moneyAmount: formatMoney({
                        money: moneyOrZero(brand?.minimum_order_amount),
                        roundingStrategy: RoundingStrategy.DROP_CENTS_ROUND_UP,
                      }),
                    })}
                  />
                </Typography>
                {viewport.isDesktopAndAbove ? <Spacer size={6} /> : null}
              </BrandDetails>
            </BrandDetailsWrapper>
            {viewport.isTablet ? (
              <TabletActions align="flex-end">{renderButtons()}</TabletActions>
            ) : null}
          </Row>

          {viewport.isMobile ? (
            <>
              <Spacer size="4x" />
              <Row align="center" justify="center">
                {renderButtons()}
              </Row>
            </>
          ) : null}

          {viewport.isDesktopAndAbove ? (
            <Spacer size="4x" />
          ) : (
            <>
              <Spacer size="3x" />
              <HorizontalLine />
              <Spacer size="3x" />
            </>
          )}

          <CapitalizedTypography
            variant={
              renderSmallerVideoName
                ? "pageHeaderSerifRegular"
                : "displaySSerifRegular"
            }
          >
            {brandVideo.name}
          </CapitalizedTypography>
          <Spacer size="1x" />
          <Typography color={Core.text.subdued}>
            <StrictLocalMsg
              defaultMessage="Created {date}"
              values={DEPRECATED_backwardsCompatibleValues({
                date: localDate(brandVideo.created_at!, "PP"),
              })}
            />
            <Duration>
              <StrictLocalMsg
                defaultMessage="{count} min"
                description={{
                  text: "min here is a short form for minutes, this represents a duration i.e. 5 min = 5 minutes",
                }}
                values={DEPRECATED_backwardsCompatibleValues({
                  count: formatVideoDuration(brandVideo.video!),
                })}
              />
            </Duration>
          </Typography>
          <Spacer size="1x" />
          <Column justify="space-between" style={{ height: "100%" }}>
            <Typography
              maxLines={useResponsiveValue({
                mobileAndAbove: 5,
                tabletAndAbove: 7,
              })}
            >
              {brandVideo.description}
            </Typography>

            {viewport.isMobile ? <Spacer size="1x" /> : null}
            <DesktopActions>{renderButtons()}</DesktopActions>
          </Column>
        </PanelContent>
      </PanelContainer>

      {showContactModal ? (
        <Suspense fallback={null}>
          <LazyContactBrandModal
            brand={brand}
            closeContactMakerModal={toggleContactModal}
          />
        </Suspense>
      ) : null}
    </>
  );
};

const PanelContainer = styled(Column)`
  position: relative;
  width: 100%;
  background-color: ${Core.surface.inverse};
  box-shadow: 0 2px 250px rgba(0, 0, 0, 0.1);
  ${responsiveMediaQueries.desktopAndAbove} {
    width: ${BRAND_PANEL_WIDTH}px;
  }
`;

const PanelContent = styled(Column)`
  padding: 32px;
  ${responsiveMediaQueries.desktopAndAbove} {
    width: ${BRAND_PANEL_WIDTH}px;
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: 24px;
  }
  ${responsiveMediaQueries.mobile} {
    padding: 16px;
  }
  height: 100%;
`;

const BrandDetailsWrapper = styled(Row)`
  flex: 1;
  ${responsiveMediaQueries.desktopAndAbove} {
    width: 100%;
  }
`;

const BrandDetails = styled(Column)`
  text-overflow: ellipsis;
  max-width: 100%;
`;

const Duration = styled.span`
  border-left: 1px solid ${Core.border.subdued};
  padding-left: 9px;
  margin-left: 9px;
`;

const StyledButton = styled(Button)`
  height: 40px;
  width: 100%;
  &,
  &:hover,
  &:active,
  &:focus {
    background-color: ${Core.surface.inverse};
    color: ${Core.text.default};
  }
  &:disabled {
    background-color: ${Core.surface.inverse};
    color: ${Core.text.subdued};
    border-color: ${Core.border.muted};
  }
  ${responsiveMediaQueries.tabletAndBelow} {
    width: 172px;
  }
  border: 1px solid ${Core.border.muted};
`;

const TabletActions = styled(Column).attrs({ flex: 1 })`
  &&& {
    ${responsiveMediaQueries.desktopAndAbove} {
      display: none;
    }
  }
}
`;

// eslint-disable-next-line @faire/prefer-slate-attrs -- use ResponsiveContentV2
const DesktopActions = styled(Row)`
  &&& {
    ${responsiveMediaQueries.tabletAndBelow} {
      display: none;
    }
  }
`;

const StyledCheckmark = styled(Checkmark).attrs({
  width: "16px",
  height: "16px",
  strokeWidth: 1,
})`
  top: 0.2em;
  position: relative;
  margin-left: 4px;
`;

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Tooltip/Tooltip#Tooltip"
 */
const StyledTooltip = styled(Tooltip)`
  text-align: left;
`;

const BrandNameWrapper = styled(Row)`
  height: 20px;
`;

// eslint-disable-next-line @faire/no-text-transform
const CapitalizedTypography = styled(Typography)`
  text-transform: capitalize;
`;
